import React, { useState, useEffect, useCallback } from 'react';
import { FaCrown, FaCheck, FaFacebook, FaTwitter, FaLinkedin, FaInstagram } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import SEOHelmet from '../SEOHelmet';
import '../../styles/landing-page/PricingPage.css';
import Navbar from './Navbar';
import ctaimage1 from '../../assets/Mobileview.png'
import ctaimage2 from '../../assets/AdminDashboard2.png'
import heroimg from '../../assets/Pricingpagehero.png'; 
const PricingPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('pricing');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const currencyRates = {
    USD: 1,
    EUR: 0.92,
    GBP: 0.79,
    INR: 83.12
  };

  const currencySymbols = {
    USD: '$',
    EUR: '€',
    GBP: '£',
    INR: '₹'
  };

  const getDefaultPlans = useCallback(() => {
    return [
      {
        _id: 1,
        name: 'Starter',
        price: 50,
        users: 5,
        assets: 1000,
        description: 'Perfect for small teams and growing organizations just starting their asset management journey. Includes essential tracking, real-time updates, and mobile access to keep your team connected. Ideal for departments, small businesses, or early-stage companies looking to streamline operations without complexity.',
        features: [
          'Asset Tracking & Monitoring',
          'Real-Time Updates',
          'Mobile App Access',
          'Barcode & QR Scanning',
          'Basic Reporting',
          'Email Notifications',
          'Email & Chat Support (Business Hours)',
          '14-Day Free Trial'
        ],
        highlighted: false,
        cta: 'Try Free'
      },
      {
        _id: 2,
        name: 'Business+',
        price: 60,
        users: 10,
        assets: 5000,
        description: 'Designed for mid-size organizations managing complex asset lifecycles across multiple departments. Advanced features like custom workflows, API access, and team collaboration enable seamless integration with your existing tech stack. Scale your operations with confidence and drive 35% improvement in asset utilization.',
        features: [
          'All Starter Features',
          'Unlimited Teams & Workspaces',
          'Custom Asset Fields',
          'Advanced Permissions & Roles',
          'API Access (REST & Webhooks)',
          'Third-Party Integrations',
          'Priority Email & Phone Support',
          'Maintenance Scheduling',
          'Depreciation Tracking',
          'Monthly Analytics Dashboard',
          'Custom Branding',
          'Audit Logs & History'
        ],
        highlighted: true,
        cta: 'Try Free'
      },
      {
        _id: 3,
        name: 'Enterprise',
        price: 0,
        users: 'Unlimited',
        assets: 'Unlimited',
        description: 'For large enterprises requiring complete control, advanced security, and dedicated support. White-label solutions, custom integrations, offline mode, and AI-powered workflows give you maximum flexibility. Our enterprise solutions are GDPR/SOC2 compliant with 99.99% uptime SLA.',
        features: [
          'All Business+ Features',
          'Unlimited Users & Assets',
          'AI-Powered Predictive Maintenance',
          'Offline Mode & Sync',
          'Multi-Tenant Architecture',
          'Advanced Security (SSO, 2FA)',
          'Dedicated Account Manager',
          'Custom Integrations (unlimited)',
          'White Label Solution',
          'Phone Support (24/7)',
          'Custom SLA & Uptime Guarantee',
          'Advanced Data Analytics',
          'On-Premise Deployment Options'
        ],
        highlighted: false,
        cta: 'Contact Sales'
      }
    ];
  }, []);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const res = await fetch(`${apiUrl}/api/plans`);
      const data = await res.json();

      if (data && data.length > 0) {
        setPlans(data);
      } else {
        setPlans(getDefaultPlans());
      }
    } catch (err) {
      console.error(err);
      setPlans(getDefaultPlans());
    } finally {
      setLoading(false);
    }
  }, [getDefaultPlans]);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const getPrice = (plan) => {
    if (!plan.price) return 'Custom';
    if (billingCycle === 'yearly') {
      return Math.round(plan.price * 12 * 0.83);
    }
    return plan.price;
  };

  return (
    <div className="pricing-page">
        <SEOHelmet page="pricing" canonicalUrl="https://www.assetflows.in/pricing" />
        <Navbar activeSection={activeSection} setActiveSection={setActiveSection} />
      {/* HEADER SECTION */}
      <header className="pricing-header">
        <div className="pricing-header__content">
          <h1>Transparent Pricing Designed for Growth</h1>
          <p>
            Choose the perfect plan for your asset management needs. Whether you're tracking 100 assets or 100,000, 
            <span className="highlight">AssetFlows</span> scales with your business. No hidden fees. No surprises. 
            Just powerful asset management that grows with you.
          </p>
        </div>
        <div className="pricing-header__image">
          <img src={heroimg} alt="Asset Management Dashboard" />
        </div>
      </header>

      {/* HERO SECTION */}
      <section className="pricing-hero">
        <h1>Simple, Flexible Pricing Plans</h1>
        <p>
          Total asset oversight in a flexible platform priced to scale with your business. 
          From startups managing their first assets to enterprises tracking thousands, we have the right plan for you. 
          Start with a 14-day free trial—no credit card required.
        </p>

        <div className="controls">
          {/* Billing Toggle */}
          <div className="billing-toggle">
            <button
              className={billingCycle === 'monthly' ? 'active' : ''}
              onClick={() => setBillingCycle('monthly')}
            >
              Monthly
            </button>
            <button
              className={billingCycle === 'yearly' ? 'active' : ''}
              onClick={() => setBillingCycle('yearly')}
            >
              Yearly (Save 17%)
            </button>
          </div>

          {/* Currency */}
          <select
            value={selectedCurrency}
            onChange={(e) => setSelectedCurrency(e.target.value)}
          >
            {Object.keys(currencySymbols).map(c => (
              <option key={c} value={c}>
                {c} ({currencySymbols[c]})
              </option>
            ))}
          </select>
        </div>
      </section>

      {/* PLANS */}
      <section className="plans-grid">
        {loading ? (
          <p>Loading plans...</p>
        ) : (
          plans.map(plan => (
            <div
              key={plan._id}
              className={`plan-card ${plan.highlighted ? 'highlighted' : ''}`}
            >
              {plan.highlighted && (
                <div className="badge">
                  <FaCrown /> MOST POPULAR
                </div>
              )}

              <h2>{plan.name}</h2>
              <p>{plan.description}</p>

              <div className="price">
                {plan.price ? (
                  <>
                    {currencySymbols[selectedCurrency]}
                    {(getPrice(plan) * currencyRates[selectedCurrency]).toFixed(0)}
                    <span>/{billingCycle === 'yearly' ? 'year' : 'month'}</span>
                  </>
                ) : (
                  'Let’s Talk'
                )}
              </div>

              <div className="meta">
                <span>👥 {plan.users}</span>
                <span>📦 {plan.assets}</span>
              </div>

              <button className="cta-btn">{plan.cta}</button>

              <ul>
                {plan.features.map((f, i) => (
                  <li key={i}>
                    <FaCheck /> {f}
                  </li>
                ))}
              </ul>
            </div>
          ))
        )}
      </section>

  

      {/* COMPARISON TABLE */}
      <section className="comparison">
        <h2>Compare Plans & Find Your Perfect Fit</h2>
        <p style={{ marginBottom: '40px', fontSize: '16px', color: '#666' }}>
          All plans include our core asset management engine. Choose the right tier for your team size, 
          asset volume, and integration needs.
        </p>
        <table>
          <thead>
            <tr>
              <th>Features</th>
              <th>Starter</th>
              <th>Business+</th>
              <th>Enterprise</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td><strong>Asset Tracking & Monitoring</strong></td>
              <td>✔ (Up to 1K assets)</td>
              <td>✔ (Up to 5K assets)</td>
              <td>✔ (Unlimited)</td>
            </tr>
            <tr>
              <td><strong>Real-Time Updates</strong></td>
              <td>✔ Standard</td>
              <td>✔ Priority</td>
              <td>✔ Instant</td>
            </tr>
            <tr>
              <td><strong>Barcode & QR Scanning</strong></td>
              <td>✔</td>
              <td>✔ Enhanced</td>
              <td>✔ Advanced</td>
            </tr>
            <tr>
              <td><strong>Mobile App Access</strong></td>
              <td>✔ iOS & Android</td>
              <td>✔ Full features</td>
              <td>✔ + Offline mode</td>
            </tr>
            <tr>
              <td><strong>Maintenance Scheduling</strong></td>
              <td>✔ Basic</td>
              <td>✔ Advanced workflow</td>
              <td>✔ AI-Predictive</td>
            </tr>
            <tr>
              <td><strong>API Access</strong></td>
              <td>—</td>
              <td>✔ REST & Webhooks</td>
              <td>✔ Full API + GraphQL</td>
            </tr>
            <tr>
              <td><strong>Custom Integrations</strong></td>
              <td>—</td>
              <td>✔ (3 integrations)</td>
              <td>✔ (Unlimited)</td>
            </tr>
            <tr>
              <td><strong>Multi-Tenant Support</strong></td>
              <td>—</td>
              <td>✔</td>
              <td>✔</td>
            </tr>
            <tr>
              <td><strong>Advanced Permissions & Roles</strong></td>
              <td>—</td>
              <td>✔ 5 custom roles</td>
              <td>✔ Unlimited roles</td>
            </tr>
            <tr>
              <td><strong>Audit Logs & History</strong></td>
              <td>—</td>
              <td>✔ 30-day history</td>
              <td>✔ Unlimited history</td>
            </tr>
            <tr>
              <td><strong>AI-Powered Analytics</strong></td>
              <td>—</td>
              <td>—</td>
              <td>✔ Predictive insights</td>
            </tr>
            <tr>
              <td><strong>Offline Mode & Sync</strong></td>
              <td>—</td>
              <td>—</td>
              <td>✔</td>
            </tr>
            <tr>
              <td><strong>Dedicated Account Manager</strong></td>
              <td>—</td>
              <td>—</td>
              <td>✔</td>
            </tr>
            <tr>
              <td><strong>Custom SLA & 99.99% Uptime</strong></td>
              <td>—</td>
              <td>—</td>
              <td>✔</td>
            </tr>
            <tr>
              <td><strong>White Label Solution</strong></td>
              <td>—</td>
              <td>—</td>
              <td>✔</td>
            </tr>
          </tbody>
        </table>
      </section>


   {/* ADDITIONAL SERVICES */}
      <section className="additional-services">
        <div className="services-header">
          <h2 className="services-title">Additional Services & Add-Ons</h2>
          <p className="services-subtitle">
            Enhance your AssetFlows experience with premium services designed for enterprise success. 
            Mix and match services to create a customized solution for your organization.
          </p>
        </div>
        <div className="services-grid">
          <div className="service-card">
            <h4 className="service-card-title">Premium Training Programs</h4>
            <p className="service-card-description">
              Expert-led virtual or on-site training sessions customized to your team's roles and workflows. 
              Includes hands-on practice, certification for power users, and follow-up training. 
              Perfect for organizations with 20+ users or complex asset management needs.
            </p>
            <div className="service-card-price">$500 per session</div>
          </div>
          <div className="service-card">
            <h4 className="service-card-title">Custom Integration Development</h4>
            <p className="service-card-description">
              Our development team builds custom connectors to your ERP, CRM, accounting, or legacy systems. 
              Includes API development, testing, documentation, and 3 months of support. 
              Eliminates manual data entry and ensures real-time synchronization between systems.
            </p>
            <div className="service-card-price">From $2,500</div>
          </div>
          <div className="service-card">
            <h4 className="service-card-title">White Label Solution</h4>
            <p className="service-card-description">
              Rebrand AssetFlows as your own with custom color schemes, logos, custom domain, and removal of all branding. 
              Perfect for resellers, managed service providers, and enterprises. Includes custom branding guidelines. 
              Your clients see your brand exclusively.
            </p>
            <div className="service-card-price">$5,000 setup + $1,000/month</div>
          </div>
          <div className="service-card">
            <h4 className="service-card-title">Custom API Development</h4>
            <p className="service-card-description">
              Our API team builds custom endpoints and workflows for your unique use cases. Supports real-time data sync, 
              batch operations, webhooks, and automated workflows. Includes 20+ hours of development and 6 months of support. 
              Scale your integrations beyond the standard API.
            </p>
            <div className="service-card-price">From $3,500</div>
          </div>
          <div className="service-card">
            <h4 className="service-card-title">Professional Data Migration</h4>
            <p className="service-card-description">
              Expert migration team handles complex data imports from legacy systems with validation and reconciliation. 
              Supports CSV, Excel, XML, and direct database connections. Includes data cleanup, deduplication, and verification. 
              Zero downtime migration ensures your operations continue uninterrupted.
            </p>
            <div className="service-card-price">$1,500 - $7,500</div>
          </div>
          <div className="service-card">
            <h4 className="service-card-title">Priority Support & SLA</h4>
            <p className="service-card-description">
              Guaranteed 1-hour response time for critical issues, dedicated Slack channel, and priority queue for all requests. 
              Includes direct access to senior support engineers and quarterly strategic reviews. Peace of mind knowing your assets 
              are always managed by our #1 priority support team.
            </p>
            <div className="service-card-price">$499/month</div>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="faq">
        <h2>Frequently Asked Questions</h2>

        <div className="faq-accordion">
          {[
            {
              id: 1,
              question: "What's included in each plan?",
              answer: "All plans include core asset tracking with real-time updates, barcode scanning, mobile app access, and email/chat support. Starter is ideal for small teams up to 1,000 assets. Business+ adds unlimited team members, custom integrations, API access, and advanced workflows for growing organizations. Enterprise includes everything plus AI-powered predictive maintenance, offline mode, 24/7 phone support, dedicated account manager, and white-label options for large enterprises."
            },
            {
              id: 2,
              question: "Can I switch plans anytime?",
              answer: "Absolutely! You can upgrade or downgrade your plan at any time with no penalties. Plan changes take effect on your next billing cycle. If you upgrade mid-cycle, we'll calculate prorated credits. If you downgrade, unused portions are credited back to your account or refunded. This flexibility means you only pay for what you need, when you need it."
            },
            {
              id: 3,
              question: "Is there a free trial? What happens after it ends?",
              answer: "Yes! All plans include a 14-day free trial with full feature access. No credit card required to start. After your trial ends, you can choose to upgrade to a paid plan, downgrade to view-only access, or cancel completely. If you upgrade during the trial, we'll credit the trial period against your first month. Your data is always yours—we never delete it."
            },
            {
              id: 4,
              question: "What's your refund and cancellation policy?",
              answer: "We offer a 30-day money-back guarantee on your first payment if you're not completely satisfied. After that, you can cancel your subscription anytime via your account dashboard with no cancellation fees. You'll retain access through the end of your current billing period. Your asset data is always available for export in CSV or JSON format."
            },
            {
              id: 5,
              question: "How do you handle users and asset limits?",
              answer: "Starter includes 5 team members (unlimited basic collaborators) and up to 1,000 assets. Business+ increases to 10 team members with support for 5,000 assets. Enterprise has unlimited users and assets. Basic collaborators at $10/month don't count toward team member limits and can be added infinitely. Need to scale? Just contact our sales team—upgrades are instant."
            },
            {
              id: 6,
              question: "Do you offer discounts for long-term commitments?",
              answer: "Yes! Our annual billing option gives you 17% off the monthly rate. For Business+ and Enterprise customers with 3-year commitments, we offer additional volume discounts starting at 20% off. Contact our sales team for a custom quote. All discounts include complimentary premium training and priority migration support."
            },
            {
              id: 7,
              question: "Can I integrate AssetFlows with my existing tools and systems?",
              answer: "Business+ includes API access and can integrate with popular ERPs, CRMs, accounting software, and other enterprise systems via REST APIs and webhooks. Enterprise customers get unlimited custom integrations plus our development team can build custom connectors. Pre-built integrations include SAP, Oracle, NetSuite, Salesforce, HubSpot, QuickBooks, and more."
            },
            {
              id: 8,
              question: "What support options are available?",
              answer: "Starter includes email and chat support during business hours with a typical 24-hour response time. Business+ adds priority support with 12-hour response time and phone access during business hours. Enterprise customers receive 24/7 phone support, a dedicated account manager, and guaranteed 1-hour response for critical issues. All plans include access to our extensive help documentation, video tutorials, and community forum with over 50,000 active users."
            }
          ].map((faq) => (
            <div
              key={faq.id}
              className={`faq-item ${expandedFAQ === faq.id ? 'active' : ''}`}
            >
              <button
                className="faq-question"
                onClick={() => setExpandedFAQ(expandedFAQ === faq.id ? null : faq.id)}
                type="button"
              >
                <span>{faq.question}</span>
                <span className="faq-arrow" />
              </button>
              {expandedFAQ === faq.id && (
                <div className="faq-answer">
                  <p>{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* BENEFITS SECTION */}
     

   

      {/* CTA */}
      <section className="pricing-cta">
        <div className="pricing-cta-content">
          <div className="pricing-cta-left">
            <h2>Ready to Transform Your Asset Management?</h2>
            <p>
              Start your 14-day free trial today. No credit card required. Join thousands of organizations 
              trusting AssetFlows with their most critical assets.
            </p>
            <button>Start Your Free Trial</button>
          </div>
          <div className="pricing-cta-right">
            <div className="cta-mockup-container">
              <div className="cta-mockup-mobile">
                <img src={ctaimage1} alt="Mobile View" />
              </div>
              <div className="cta-mockup-laptop">
                <img src={ctaimage2} alt="Laptop View" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER SECTION */}
      <footer className="pricing-footer">
        <div className="footer-content">
          <div className="footer-grid">
            {/* Brand Section */}
            <div className="footer-section">
              <div className="footer-brand">
                <span>📦</span>
                <span>AssetFlows</span>
              </div>
              <p className="footer-description">
                The most comprehensive, user-friendly asset management platform trusted by 5,000+ organizations worldwide. 
                From startups to Fortune 500 companies, AssetFlows is the smart choice for intelligent asset management, 
                maintenance tracking, and operational excellence.
              </p>
              <div className="footer-social">
                <button onClick={() => {}} title="Facebook" className="icon-btn"><FaFacebook /></button>
                <button onClick={() => {}} title="Twitter" className="icon-btn"><FaTwitter /></button>
                <button onClick={() => {}} title="LinkedIn" className="icon-btn"><FaLinkedin /></button>
                <button onClick={() => {}} title="Instagram" className="icon-btn"><FaInstagram /></button>
              </div>
            </div>

            {/* Product Section */}
            <div className="footer-section">
              <h4>Product</h4>
              <ul>
                <li><a href="/pricing" onClick={() => navigate('/pricing')}>Pricing</a></li>
                <li><a href="/why-assetflow" onClick={() => navigate('/why-assetflow')}>Why AssetFlows</a></li>
                <li><span>Features</span></li>
                <li><span>Security</span></li>
                <li><span>Integrations</span></li>
              </ul>
            </div>

            {/* Company Section */}
            <div className="footer-section">
              <h4>Company</h4>
              <ul>
                <li><span>About Us</span></li>
                <li><span>Blog</span></li>
                <li><span>Careers</span></li>
                <li><span>Contact</span></li>
                <li><span>Partners</span></li>
              </ul>
            </div>

            {/* Resources Section */}
            <div className="footer-section">
              <h4>Resources</h4>
              <ul>
                <li><span>Documentation</span></li>
                <li><span>Support</span></li>
                <li><span>FAQ</span></li>
                <li><span>Community</span></li>
                <li><span>Status</span></li>
              </ul>
            </div>
          </div>

          {/* Divider */}
          <div className="footer-divider"></div>

          {/* Bottom Footer */}
          <div className="footer-bottom">
            <p>&copy; 2024 AssetFlows. All rights reserved.</p>
            <div className="footer-links">
              <button onClick={() => {}} className="footer-link-btn">Privacy Policy</button>
              <button onClick={() => {}} className="footer-link-btn">Terms of Service</button>
              <button onClick={() => {}} className="footer-link-btn">Cookie Policy</button>
              <button onClick={() => {}} className="footer-link-btn">Security</button>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PricingPage;